import { useMemo, useState } from "react";

import useCategoriesPartial from "../../data/useCategoriesPartial";
import usePartCategorySubParts from "../../data/usePartCategorySubParts";
import useNotification from "../notifications/useNotifications";
import Modal from "../../layouts/Modal";
import { IPartCategory } from "../../models/Part";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";

interface IPartCategorySubPartsEditorProps {
  category: IPartCategory | undefined;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function PartCategorySubPartsEditor({ category, open, setOpen }: IPartCategorySubPartsEditorProps) {
  const { categories: allCategories } = useCategoriesPartial();
  const { subParts, subPartLinks, toggleSubPart } = usePartCategorySubParts(category?.id);
  const { addNotification } = useNotification();

  const [searchTerm, setSearchTerm] = useState("");
  const [onlySelected, setOnlySelected] = useState(false);

  const isLoading = allCategories.isLoading || subParts.isLoading;
  const categoryId = category?.id;

  /** Sub-part category ids currently attached to this main category. */
  const attachedIds = useMemo(
    () => new Set((subParts.data?.data ?? []).map((s) => s.subPartCategoryId)),
    [subParts.data?.data]
  );

  /**
   * Categories that already list THIS category as one of their sub-parts. Attaching the
   * inverse would make the pair conflict permanently with no way to resolve it, so the
   * backend refuses it - we just don't offer it.
   */
  const inverseMainIds = useMemo(() => {
    if (!categoryId) return new Set<string>();
    return new Set(
      (subPartLinks.data?.data ?? [])
        .filter((l) => l.subPartCategoryId === categoryId)
        .map((l) => l.mainCategoryId)
    );
  }, [subPartLinks.data?.data, categoryId]);

  const candidates = useMemo(() => {
    if (!categoryId) return [];

    let list = (allCategories.data?.data ?? []).filter((c) => c.id !== categoryId);

    if (onlySelected) {
      list = list.filter((c) => attachedIds.has(c.id));
    }

    if (searchTerm) {
      list = list.filter((c) => c.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    // Attached first, then everything else alphabetically.
    return [...list].sort((a, b) => {
      const aAttached = attachedIds.has(a.id);
      const bAttached = attachedIds.has(b.id);
      if (aAttached && !bAttached) return -1;
      if (bAttached && !aAttached) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [allCategories.data?.data, categoryId, attachedIds, onlySelected, searchTerm]);

  const handleToggle = (subPartCategoryId: string) => {
    if (!category) return;

    toggleSubPart.mutate(
      { mainCategoryId: category.id, subPartCategoryId },
      {
        onSuccess: () => {
          addNotification({ variant: "success", primaryText: "Sub-parts updated" });
        },
        onError: (error: any) => {
          // The API returns a specific reason for self-links and inverse links,
          // so surface it rather than a generic failure.
          addNotification({
            variant: "error",
            primaryText: error?.message ?? "Failed to update sub-parts",
          });
        },
      }
    );
  };

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-4xl">
      <div className="flex flex-col gap-y-2">
        <h2 className="text-2xl font-semibold">{category?.name} Sub-Parts</h2>
        <p className="text-sm text-gray-600">
          Categories attached here are included in {category?.name ?? "this category"}. Selecting
          both on the same job will be flagged as a conflict.
        </p>

        <div className="flex items-center flex-row gap-x-4 mt-2">
          <input
            type="text"
            placeholder="Search Categories"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="p-2"
          />
          <label>Hide Unselected</label>
          <input
            type="checkbox"
            checked={onlySelected}
            onChange={(e) => setOnlySelected(e.target.checked)}
            className="p-2"
          />
          <span className="ml-auto text-sm text-gray-500">{attachedIds.size} attached</span>
        </div>

        <div className="flex flex-col max-h-[40vh] overflow-y-scroll border border-slate-300 rounded-md mt-2">
          {isLoading ? <LoadingWheel /> :
            candidates.map((candidate) => {
              const isAttached = attachedIds.has(candidate.id);
              const isInverse = inverseMainIds.has(candidate.id);

              return (
                <div key={candidate.id} className="p-2 hover:bg-slate-200 flex items-center flex-row gap-x-4">
                  <ButtonInput
                    isSubmit={false}
                    label={isAttached ? "-" : "+"}
                    onClick={() => handleToggle(candidate.id)}
                    classes={"h-fit self-end"}
                    disabled={isInverse}
                  />
                  <p>{candidate.name}</p>
                  {isInverse && (
                    <span className="rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase leading-tight text-gray-600">
                      Already lists this as a sub-part
                    </span>
                  )}
                </div>
              );
            })
          }
        </div>
      </div>
    </Modal>
  );
}
